import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Alert,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { reportsService } from '../../services/api';
import {
  DeliveryNeighborhoodReport,
  DeliveryNeighborhoodReportItem,
  DeliveryNeighborhoodReportDetail,
} from '../../types/reports';
import { StatusPedido } from '../../types/pedido';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate } from 'react-router-dom';
import { SelectChangeEvent } from '@mui/material/Select';
import { deliveryCourierService, type DeliveryCourier } from '../../services';

interface FiltersState {
  dataInicio: string;
  dataFim: string;
  status: StatusPedido[];
  telefone: string;
  courierId: string;
}

const formatDateInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const STATUS_OPTIONS: { value: StatusPedido; label: string; description: string }[] = [
  { value: StatusPedido.RECEBIDO, label: 'Recebido', description: 'Pedidos recebidos e aguardando preparo' },
  { value: StatusPedido.EM_PREPARO, label: 'Em preparo', description: 'Pedidos em preparação' },
  { value: StatusPedido.PRONTO, label: 'Pronto', description: 'Pedidos prontos para entrega' },
  { value: StatusPedido.EM_ROTA, label: 'Em rota', description: 'Pedidos em rota de entrega' },
  { value: StatusPedido.ENTREGUE, label: 'Entregue', description: 'Pedidos entregues ao cliente' },
  { value: StatusPedido.CANCELADO, label: 'Cancelado', description: 'Pedidos cancelados' },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const DeliveryNeighborhoodReportPage: React.FC = () => {
  const [filters, setFilters] = useState<FiltersState>(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);

    return {
      dataInicio: formatDateInput(firstDay),
      dataFim: formatDateInput(now),
      status: STATUS_OPTIONS.map((option) => option.value),
      telefone: '',
      courierId: '',
    };
  });
  const [viewMode, setViewMode] = useState<'agregado' | 'analitico'>('agregado');
  const navigate = useNavigate();
  const [couriers, setCouriers] = useState<DeliveryCourier[]>([]);
  const [couriersLoading, setCouriersLoading] = useState(false);

  const queryParams = useMemo(() => {
    const params: {
      dataInicio?: string;
      dataFim?: string;
      status?: string[];
      telefone?: string;
      courierId?: string;
    } = {};

    if (filters.dataInicio) {
      params.dataInicio = new Date(`${filters.dataInicio}T00:00:00`).toISOString();
    }
    if (filters.dataFim) {
      params.dataFim = new Date(`${filters.dataFim}T23:59:59`).toISOString();
    }
    if (filters.status.length > 0) {
      params.status = filters.status;
    }
    if (filters.telefone.trim()) {
      params.telefone = filters.telefone.replace(/\D/g, '');
    }
    if (filters.courierId) {
      params.courierId = filters.courierId;
    }

    return params;
  }, [filters]);

  useEffect(() => {
    let active = true;

    const loadCouriers = async () => {
      try {
        setCouriersLoading(true);
        const data = await deliveryCourierService.list({ isActive: true });
        if (!active) return;

        setCouriers(data);

        setFilters((prev) => {
          const hasSelected = prev.courierId ? data.some((courier) => courier.id === prev.courierId) : false;

          if (data.length === 1 && (!prev.courierId || prev.courierId !== data[0].id)) {
            return {
              ...prev,
              courierId: data[0].id,
            };
          }

          if (prev.courierId && !hasSelected) {
            return {
              ...prev,
              courierId: '',
            };
          }

          return prev;
        });
      } catch (error) {
        if (!active) return;
        console.error('[DeliveryNeighborhoodReportPage] loadCouriers error', error);
        setCouriers([]);
      } finally {
        if (active) {
          setCouriersLoading(false);
        }
      }
    };

    void loadCouriers();

    return () => {
      active = false;
    };
  }, []);

  const { data, isLoading, isError, isFetching } = useQuery<DeliveryNeighborhoodReport>({
    queryKey: ['deliveries-report', queryParams],
    queryFn: () => reportsService.getDeliveriesByNeighborhood(queryParams),
    enabled: filters.status.length > 0,
  });

  const handleStatusChange = (_event: React.MouseEvent<HTMLElement>, value: StatusPedido[]) => {
    if (value.length === 0) {
      return;
    }
    setFilters((prev) => ({
      ...prev,
      status: value,
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCourierChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      courierId: value,
    }));
  };

  const totalizadores = useMemo(() => {
    const totalPedidos = data?.totalPedidos ?? 0;
    const totalTaxaEntrega = data?.totalTaxaEntrega ?? 0;
    const mediaTaxa = totalPedidos > 0 ? totalTaxaEntrega / totalPedidos : 0;

    return {
      totalPedidos,
      totalTaxaEntrega,
      mediaTaxa,
    };
  }, [data]);

  const agregados = data?.agregados ?? [];
  const detalhes = data?.detalhes ?? [];

  const handleNavigateToPrint = () => {
    const params = new URLSearchParams();
    if (filters.dataInicio) params.set('dataInicio', filters.dataInicio);
    if (filters.dataFim) params.set('dataFim', filters.dataFim);
    filters.status.forEach((status) => params.append('status', status));
    if (filters.telefone.trim()) params.set('telefone', filters.telefone.replace(/\D/g, ''));
    if (filters.courierId) params.set('courierId', filters.courierId);
    params.set('viewMode', viewMode);

    navigate(`/reports/deliveries-neighborhoods/print?${params.toString()}`);
  };

  const renderStatusSummary = () => (
    <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
      {filters.status.map((status) => {
        const option = STATUS_OPTIONS.find((item) => item.value === status);
        if (!option) {
          return null;
        }
        return (
          <Chip
            key={status}
            label={option.label}
            color={status === StatusPedido.ENTREGUE ? 'success' : status === StatusPedido.CANCELADO ? 'error' : 'default'}
            variant="outlined"
          />
        );
      })}
    </Box>
  );

  const renderEmptyState = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Nenhum dado encontrado
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ajuste os filtros para visualizar entregas registradas no período selecionado.
        </Typography>
      </CardContent>
    </Card>
  );

  const renderAggregatedTable = () => {
    if (agregados.length === 0) {
      return renderEmptyState();
    }

    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6">Resumo por bairro</Typography>
              <Typography variant="body2" color="text.secondary">
                Valores agrupados considerando todas as entregas filtradas.
              </Typography>
            </Box>
            {isFetching && <CircularProgress size={24} />}
          </Box>
          <Box component="table" width="100%" sx={{ borderCollapse: 'collapse' }}>
            <Box component="thead" sx={{ backgroundColor: 'background.default' }}>
              <Box component="tr">
                <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1.5 }}>Bairro</Box>
                <Box component="th" sx={{ textAlign: 'right', py: 1, px: 1.5 }}>Quantidade de entregas</Box>
                <Box component="th" sx={{ textAlign: 'right', py: 1, px: 1.5 }}>Taxa do bairro</Box>
                <Box component="th" sx={{ textAlign: 'right', py: 1, px: 1.5 }}>Total de taxas</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {agregados.map((item: DeliveryNeighborhoodReportItem) => (
                <Box component="tr" key={item.bairro} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'background.paper' } }}>
                  <Box component="td" sx={{ py: 1, px: 1.5 }}>{item.bairro}</Box>
                  <Box component="td" sx={{ py: 1, px: 1.5, textAlign: 'right' }}>{item.quantidadePedidos}</Box>
                  <Box component="td" sx={{ py: 1, px: 1.5, textAlign: 'right' }}>{formatCurrency(item.taxaDoBairro)}</Box>
                  <Box component="td" sx={{ py: 1, px: 1.5, textAlign: 'right' }}>{formatCurrency(item.valorTotalTaxas)}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderAnalyticTable = () => {
    if (detalhes.length === 0) {
      return renderEmptyState();
    }

    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6">Entregas detalhadas</Typography>
              <Typography variant="body2" color="text.secondary">
                Cada linha representa uma entrega concluída no período filtrado.
              </Typography>
            </Box>
            {isFetching && <CircularProgress size={24} />}
          </Box>
          <Box component="table" width="100%" sx={{ borderCollapse: 'collapse' }}>
            <Box component="thead" sx={{ backgroundColor: 'background.default' }}>
              <Box component="tr">
                <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1.5 }}>Data</Box>
                <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1.5 }}>Bairro</Box>
                <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1.5 }}>Cliente</Box>
                <Box component="th" sx={{ textAlign: 'right', py: 1, px: 1.5 }}>Taxa</Box>
                <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1.5 }}>Status</Box>
                <Box component="th" sx={{ textAlign: 'left', py: 1, px: 1.5 }}>Entregador</Box>
              </Box>
            </Box>
            <Box component="tbody">
              {detalhes.map((item: DeliveryNeighborhoodReportDetail) => (
                <Box component="tr" key={item.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'background.paper' } }}>
                  <Box component="td" sx={{ py: 1, px: 1.5 }}>{formatDateTime(item.dataPedido)}</Box>
                  <Box component="td" sx={{ py: 1, px: 1.5 }}>{item.bairro}</Box>
                  <Box component="td" sx={{ py: 1, px: 1.5 }}>{item.cliente}</Box>
                  <Box component="td" sx={{ py: 1, px: 1.5, textAlign: 'right' }}>{formatCurrency(item.taxaEntrega)}</Box>
                  <Box component="td" sx={{ py: 1, px: 1.5 }}>{item.status}</Box>
                  <Box component="td" sx={{ py: 1, px: 1.5 }}>{item.courierName || '—'}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        flexWrap="wrap"
        gap={2}
        className="screen-only"
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Relatório de entregas por bairro
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Analise os pedidos entregues por bairro para facilitar o acompanhamento e pagamento dos entregadores.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handleNavigateToPrint}
          disabled={isLoading || isFetching}
          sx={{
            alignSelf: { xs: 'flex-start', md: 'center' },
          }}
        >
          Imprimir
        </Button>
      </Box>

      <Card className="screen-only">
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Data inicial"
                name="dataInicio"
                type="date"
                value={filters.dataInicio}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Data final"
                name="dataFim"
                type="date"
                value={filters.dataFim}
                onChange={handleInputChange}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Telefone do cliente"
                name="telefone"
                value={filters.telefone}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="Apenas números"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small" disabled={couriersLoading || couriers.length === 0}>
                <InputLabel id="courier-filter-label">Entregador</InputLabel>
                <Select
                  labelId="courier-filter-label"
                  value={filters.courierId && couriers.some((c) => c.id === filters.courierId) ? filters.courierId : ''}
                  label="Entregador"
                  onChange={handleCourierChange}
                  endAdornment={couriersLoading ? <CircularProgress size={18} sx={{ mr: 2 }} /> : undefined}
                >
                  {couriers.length > 1 && (
                    <MenuItem value="">
                      <em>Todos os entregadores</em>
                    </MenuItem>
                  )}
                  {couriers.map((courier) => (
                    <MenuItem key={courier.id} value={courier.id}>
                      {courier.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {couriersLoading
                    ? 'Carregando entregadores ativos...'
                    : couriers.length === 0
                    ? 'Nenhum entregador ativo encontrado. Cadastre um entregador para filtrar.'
                    : filters.courierId
                    ? 'Mostrar apenas entregas realizadas por este entregador.'
                    : couriers.length === 1
                    ? 'Aplicado automaticamente: único entregador ativo disponível.'
                    : 'Selecione um entregador específico ou deixe em branco para todos.'}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1">Status dos pedidos</Typography>
              <Typography variant="body2" color="text.secondary">
                Selecione quais status devem ser considerados no relatório. Por padrão mostramos apenas pedidos entregues.
              </Typography>
              <ToggleButtonGroup
                value={filters.status}
                onChange={handleStatusChange}
                color="primary"
                size="small"
                sx={{ mt: 1, flexWrap: 'wrap' }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value} size="small" sx={{ textTransform: 'none', m: 0.5 }}>
                    {option.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {renderStatusSummary()}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box className="screen-only">
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total de pedidos
                </Typography>
                <Typography variant="h4">{totalizadores.totalPedidos}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total em taxas de entrega
                </Typography>
                <Typography variant="h4">{formatCurrency(totalizadores.totalTaxaEntrega)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Taxa média por entrega
                </Typography>
                <Typography variant="h4">{formatCurrency(totalizadores.mediaTaxa)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {isError && (
        <Alert severity="error">
          Ocorreu um erro ao carregar o relatório. Tente ajustar os filtros ou recarregar a página.
        </Alert>
      )}

      <Card sx={{ mb: 2 }} className="screen-only">
        <Tabs
          value={viewMode}
          onChange={(_, value) => setViewMode(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab value="agregado" label="Resumo por bairro" />
          <Tab value="analitico" label="Entregas detalhadas" />
        </Tabs>
      </Card>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={240} className="screen-only">
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Box className="screen-only">{viewMode === 'agregado' ? renderAggregatedTable() : renderAnalyticTable()}</Box>
      )}
    </Box>
  );
};

export { DeliveryNeighborhoodReportPage };
export default DeliveryNeighborhoodReportPage;
